import { openIndexedDBWithRecovery } from "../../common/indexDB";
import Language from "../../language/Language";
import { SpeakingRate } from "../../voice/model/SpeakingRate";
import { EnglishPronunciation, VoiceGender } from "../../voice/util/voice";

const recentClickedLanguagesKey = "RecentClickedLanguagesKey";
const voiceGenderkey = "VoiceGenderKey";
const englishPronunciationKey = "EnglishPronunciationKey";
const speakingRateKey = "SpeakingRateKey";

export function getRecentClickedLanguagesFromLocalStroage(): Language[] {
  const items = localStorage.getItem(recentClickedLanguagesKey);
  return items ? JSON.parse(items) : [];
}

export function setRecentClickedLanguagesToLocalStorage(codes: Language[]) {
  localStorage.setItem(recentClickedLanguagesKey, JSON.stringify(codes));
}

export function getSpeakingRateFromLocalStorage(): SpeakingRate {
  const value = localStorage.getItem(speakingRateKey);
  if (value && Object.values(SpeakingRate).includes(value as SpeakingRate)) {
    return value as SpeakingRate;
  }

  setSpeakingRateToLocalStorage(SpeakingRate.normal);
  return SpeakingRate.normal;
}

export function setSpeakingRateToLocalStorage(rate: SpeakingRate) {
  localStorage.setItem(speakingRateKey, rate);
}

export function getEnglishPronunciationFromLocalStorage(): EnglishPronunciation {
  const value = localStorage.getItem(englishPronunciationKey);

  if (
    value &&
    Object.values(EnglishPronunciation).includes(value as EnglishPronunciation)
  ) {
    return value as EnglishPronunciation;
  }

  setEnglishPronunciationToLocalStorage(EnglishPronunciation.america);
  return EnglishPronunciation.america;
}

export function setEnglishPronunciationToLocalStorage(
  pronunciation: EnglishPronunciation
) {
  localStorage.setItem(englishPronunciationKey, pronunciation);
}

export function getVoiceGenderFromLocalStroage(): VoiceGender {
  const value = localStorage.getItem(voiceGenderkey);

  if (value && Object.values(VoiceGender).includes(value as VoiceGender)) {
    return value as VoiceGender;
  }

  setVoiceGenderToLocalStroage(VoiceGender.female);
  return VoiceGender.female;
}

export function setVoiceGenderToLocalStroage(gender: VoiceGender) {
  localStorage.setItem(voiceGenderkey, gender);
}

const DB_NAME = "voiceCacheDB";
const STORE_NAME = "voices";
const DB_VERSION = 3;

export async function setCachedVoiceToIndexedDB(
  text: string,
  languageCode: string,
  voiceType: string,
  file: string,
  speakingRate: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = function () {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = function () {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const id = `text:${text}_languageCode:${languageCode}_voiceType:${voiceType}_speakingRate:${speakingRate}`;
      const data = { id, file };

      const putRequest = store.put(data);
      putRequest.onsuccess = function () {
        resolve();
      };
      putRequest.onerror = function () {
        reject(putRequest.error);
      };
    };

    request.onerror = function () {
      reject(request.error);
    };
  });
}

export async function getCachedVoiceFromIndexedDB(
  text: string,
  languageCode: string,
  voiceType: string,
  speakingRate: number
): Promise<string | null> {
  try {
    const db = await openIndexedDBWithRecovery(DB_NAME, STORE_NAME, DB_VERSION);

    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const id = `text:${text}_languageCode:${languageCode}_voiceType:${voiceType}_speakingRate:${speakingRate}`;

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = function () {
        if (getRequest.result) {
          resolve(getRequest.result.file);
        } else {
          resolve(null);
        }
      };

      getRequest.onerror = function () {
        reject(getRequest.error);
      };
    });
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function getCachedLanguageCodeFromIndexedDB(
  text: string
): Promise<string | null> {
  try {
    const db = await openIndexedDBWithRecovery(DB_NAME, STORE_NAME, DB_VERSION);

    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);

    const id = `textLanguage:${text}`;
    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = function () {
        if (getRequest.result) {
          resolve(getRequest.result.languageCode);
        } else {
          resolve(null);
        }
      };

      getRequest.onerror = function () {
        reject(getRequest.error);
      };
    });
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function setCachedLanguageCodeToIndexedDB(
  text: string,
  languageCode: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = function () {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = function () {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const id = `textLanguage:${text}`;
      const data = { id, languageCode };

      const putRequest = store.put(data);
      putRequest.onsuccess = function () {
        resolve();
      };
      putRequest.onerror = function () {
        reject(putRequest.error);
      };
    };

    request.onerror = function () {
      reject(request.error);
    };
  });
}
